import { ShoppingBag } from 'lucide-react'

import type { ReactNode } from 'react'
import { AnimatedThemeToggler } from '@/components/ui/animated-theme-toggler'

interface AuthLayoutProps {
  children: ReactNode
  title?: string
  subtitle?: string
  showBranding?: boolean
  showThemeToggle?: boolean
  showFooter?: boolean
  backgroundVariant?: 'gradient' | 'pattern' | 'solid'
  maxWidth?: 'sm' | 'md' | 'lg'
}

function ThemeToggle() {
  return <AnimatedThemeToggler />
}

function BrandingSection({
  title,
  subtitle,
}: {
  title?: string
  subtitle?: string
}) {
  return (
    <div className="text-center space-y-4 mb-8">
      {/* Logo */}
      <div className="flex justify-center">
        <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl">
          <ShoppingBag className="h-8 w-8 text-primary" />
        </div>
      </div>

      {/* Company Name */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">
          AI Shopping Assistant
        </h1>
        <p className="text-muted-foreground text-sm">
          Intelligent product recommendations platform
        </p>
      </div>

      {/* Page Title & Subtitle */}
      {(title || subtitle) && (
        <div className="space-y-1 pt-4 border-t border-border/50">
          {title && <h2 className="text-xl font-semibold">{title}</h2>}
          {subtitle && (
            <p className="text-muted-foreground text-sm">{subtitle}</p>
          )}
        </div>
      )}
    </div>
  )
}

function BackgroundPattern() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />

      {/* Geometric patterns */}
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary/5 rounded-full blur-3xl" />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: '24px 24px',
        }}
      />
    </div>
  )
}

function Footer() {
  return (
    <footer className="mt-8 pt-6 border-t border-border/50">
      <div className="text-center space-y-4">
        {/* Links */}
        <div className="flex justify-center space-x-6 text-sm">
          <button className="text-muted-foreground hover:text-foreground transition-[color,box-shadow] focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] rounded">
            Privacy Policy
          </button>
          <button className="text-muted-foreground hover:text-foreground transition-[color,box-shadow] focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] rounded">
            Terms of Service
          </button>
          <button className="text-muted-foreground hover:text-foreground transition-[color,box-shadow] focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] rounded">
            Support
          </button>
        </div>

        {/* Copyright */}
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} AI Shopping Assistant. All rights
          reserved.
        </p>
      </div>
    </footer>
  )
}

export function AuthLayout({
  children,
  title,
  subtitle,
  showBranding = true,
  showThemeToggle = true,
  showFooter = true,
  backgroundVariant = 'pattern',
  maxWidth = 'md',
}: AuthLayoutProps) {
  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
  }

  const backgroundClasses = {
    gradient: 'bg-gradient-to-br from-background via-background to-muted/20',
    pattern: 'bg-background',
    solid: 'bg-background',
  }

  return (
    <div
      className={`min-h-screen ${backgroundClasses[backgroundVariant]} relative`}
    >
      {/* Background Elements */}
      {backgroundVariant === 'pattern' && <BackgroundPattern />}

      {/* Theme Toggle */}
      {showThemeToggle && (
        <div className="absolute top-4 right-4 z-10">
          <ThemeToggle />
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-0 flex items-center justify-center min-h-screen p-4">
        <div className={`w-full ${maxWidthClasses[maxWidth]} space-y-6`}>
          {/* Branding Section */}
          {showBranding && (
            <BrandingSection title={title} subtitle={subtitle} />
          )}

          {/* Auth Form Content */}
          <div className="relative">{children}</div>

          {/* Footer */}
          {showFooter && <Footer />}
        </div>
      </div>

      {/* Skip to main content for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-primary-foreground px-4 py-2 rounded-md z-50"
      >
        Skip to main content
      </a>
    </div>
  )
}

// Preset layouts for common auth pages
export function LoginLayout({ children }: { children: ReactNode }) {
  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to your account to continue"
    >
      {children}
    </AuthLayout>
  )
}

export function RegisterLayout({ children }: { children: ReactNode }) {
  return (
    <AuthLayout
      title="Create your account"
      subtitle="Get started with your AI shopping assistant"
    >
      {children}
    </AuthLayout>
  )
}

export function ForgotPasswordLayout({ children }: { children: ReactNode }) {
  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email to receive a password reset link"
    >
      {children}
    </AuthLayout>
  )
}

export function VerifyEmailLayout({ children }: { children: ReactNode }) {
  return (
    <AuthLayout
      title="Email Verification"
      subtitle="Confirming your email address"
      showBranding={false}
    >
      {children}
    </AuthLayout>
  )
}

export function ResetPasswordLayout({ children }: { children: ReactNode }) {
  return (
    <AuthLayout
      title="Set New Password"
      subtitle="Enter your new password below"
      showBranding={false}
    >
      {children}
    </AuthLayout>
  )
}

// Export types for external use
export type { AuthLayoutProps }
